/**
 * @file FooterLinksGroup.tsx
 * @description Подгруппа «Ссылки» нижней полосы подвала (phase19.16, приёмка
 * Р2а — SB118, SB121; партия «Юридические документы» — SB142;
 * uiux-legal-docs §5, uiux-legal-steps-b-c §8.2): общий список ссылок
 * платформы и своих строк, «+ Добавить ссылку на документ», подсказка под
 * блоком и мягкое предупреждение после трёх своих строк
 * (`footerDocsWarnings`, предупреждение, а не запрет — SB120).
 *
 * Свои строки — раздел темы `footerDocs` (не `footer.legal`): правка
 * порядка пишет оба раздела одной правкой черновика (`onLinks` с
 * `legal` и `docs`) — два отдельных вызова затёрли бы друг друга.
 */

import {
  FOOTER_DOCS_MAX,
  FOOTER_DOCS_WARNING_MESSAGES,
  FOOTER_HIDEABLE_LINKS,
  footerDocItemKey,
  footerDocsWarnings,
  type FooterDocItem,
  orderFooterPlatformLinks,
  type FooterPlatformLink,
  type SiteFooterDocs,
  type SiteFooterLegal,
} from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, SettingsSubgroup } from "../../ui/settings-groups";
import { AddFooterDocDialog, type FooterDocPick } from "./AddFooterDocDialog";
import { FooterLinksOrder } from "./FooterLinksOrder";
import type { LegalTarget } from "../../../lib/api-legal-targets";
import { footerDocRowView, type FooterLinkWhere } from "../../../lib/site-view/footer-link-where";
import { ALL_DOCUMENTS_VALUE } from "../../../lib/site-view/legal-targets";

/** Правка «Ссылок»: порядок и выключение платформы (`legal`) и/или свои строки (`docs`). */
export type FooterLinksPatch = { legal?: SiteFooterLegal; docs?: SiteFooterDocs };

interface Props {
  value: SiteFooterLegal;
  docs: SiteFooterDocs;
  onLinks: (patch: FooterLinksPatch) => void;
  hiddenLinks: ReadonlySet<FooterPlatformLink>;
  where?: Readonly<Record<FooterPlatformLink, FooterLinkWhere>>;
  /** Документы и группы магазина; null — не загружены. */
  legalTargets: LegalTarget[] | null;
  /** Перечитать документы после добавления (создание группы сертификатов). */
  onLegalReload?: () => void;
  /** `/shop/<slug>/settings/legal-documents`. */
  legalDocsHref: string;
}

/** Ключи «уже в Ссылках» для окна: id документов и «Все документы». */
function takenKeys(items: readonly FooterDocItem[]): Set<string> {
  return new Set(items.map((item) => (item.kind === "document" ? item.documentId : ALL_DOCUMENTS_VALUE)));
}

export function FooterLinksGroup({ value, docs, onLinks, hiddenLinks, where, legalTargets, onLegalReload, legalDocsHref }: Props) {
  const [adding, setAdding] = useState(false);
  const items = docs.items;
  const warning = footerDocsWarnings(items)[0] ?? null;
  const full = items.length >= FOOTER_DOCS_MAX;

  const setItems = (next: FooterDocItem[]) => onLinks({ docs: { ...docs, items: next } });

  function add(pick: FooterDocPick) {
    // Новая строка — в конец списка (после последней ссылки платформы), включённой.
    const last = orderFooterPlatformLinks(value.linksOrder).at(-1) ?? null;
    const item: FooterDocItem = pick.kind === "document" ? { kind: "document", documentId: pick.documentId, enabled: true, after: last } : { kind: "allDocuments", enabled: true, after: last };
    setItems([...items, item]);
    setAdding(false);
    onLegalReload?.();
  }

  return (
    <SettingsSubgroup title="Ссылки">
      <div className="flex flex-col gap-2">
        <FooterLinksOrder
          linksOrder={value.linksOrder}
          items={items}
          duplicated={hiddenLinks}
          switchedOff={new Set(value.hiddenLinks)}
          where={where}
          docView={(item) => footerDocRowView(item, legalTargets, legalDocsHref)}
          // Порядок платформы и якоря своих строк — одной правкой.
          onChange={(next) => onLinks({ legal: { ...value, linksOrder: next.linksOrder }, docs: { ...docs, items: next.items } })}
          // Выключение (SB118 дополнение): список выключенных в порядке платформы.
          onToggle={(link, show) =>
            onLinks({ legal: { ...value, hiddenLinks: FOOTER_HIDEABLE_LINKS.filter((h) => (h === link ? !show : value.hiddenLinks.includes(h))) } })
          }
          onToggleDoc={(key, on) => setItems(items.map((item) => (footerDocItemKey(item) === key ? { ...item, enabled: on } : item)))}
          onRemoveDoc={(key) => setItems(items.filter((item) => footerDocItemKey(item) !== key))}
        />
        <Button variant="outline" className="self-start" disabled={full} onClick={() => setAdding(true)}>
          <Plus aria-hidden className="size-4" />
          Добавить ссылку на документ
        </Button>
        {full ? <FieldHint className="mt-0">В «Ссылках» не больше {FOOTER_DOCS_MAX} своих строк.</FieldHint> : null}
        {warning ? <NoticeStrip tone="warning">{FOOTER_DOCS_WARNING_MESSAGES[warning.code]}</NoticeStrip> : null}
        <FieldHint className="mt-0">Оферта и политика ПДн на сайте всегда; остальные — если опубликованы и не выключены.</FieldHint>
        <FieldHint className="mt-0">Ссылки — одна строка внизу, у сведений о продавце. Для колонок с заголовками — меню подвала.</FieldHint>
      </div>
      {adding ? <AddFooterDocDialog taken={takenKeys(items)} onAdd={add} onClose={() => setAdding(false)} /> : null}
    </SettingsSubgroup>
  );
}
